import { useEffect, useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'

type TuningProfileNameDialogProps = {
  open: boolean
  // add saves the trail system's current values as a new profile; rename only changes the name
  mode: 'add' | 'rename'
  trailSystemName: string
  trailCount?: number
  initialName?: string
  activeProfileName?: string
  isSaving: boolean
  onCancel: () => void
  onSave: (name: string) => void
}
/**
 * @description Asks for a tuning profile's name, when saving the current values as a new profile or renaming one.
 */
const TuningProfileNameDialog = (props: TuningProfileNameDialogProps) => {
  const { open, mode, trailSystemName, trailCount, initialName, activeProfileName, isSaving, onCancel, onSave } = props
  const [name, setName] = useState('')

  useEffect(() => {
    if (open) {
      setName(initialName ?? '')
    }
  }, [open, initialName])

  const trimmedName = name.trim()

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={isSaving ? undefined : onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmedName && !isSaving) {
            onSave(trimmedName)
          }
        }}
      >
        <DialogTitle>{mode === 'add' ? 'New Tuning Profile' : 'Rename Profile'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <TextField
              autoFocus
              fullWidth
              required
              label="Profile Name"
              placeholder="e.g. Winter"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 100 }}
              helperText="Must be unique within this trail system."
            />
            {mode === 'add' && (
              <Alert severity="info">
                Starts as a copy of the current resistance, precipitation lookback, saturation and dry-time values for{' '}
                {trailSystemName}
                {typeof trailCount === 'number'
                  ? ` and its ${trailCount} ${trailCount === 1 ? 'trail' : 'trails'}`
                  : ''}
                {activeProfileName
                  ? `. You'll edit it next. ${activeProfileName} stays active and nothing changes on the trails until you apply the new profile.`
                  : '. It becomes the active profile, so your edits save into it.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!trimmedName || isSaving}>
            {mode === 'add' ? 'Create Profile' : 'Rename'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
export default TuningProfileNameDialog
